import { useMemo, useState } from 'react';
import { MagnifyingGlass, Trash, UserPlus, UserSwitch, Warning } from '@phosphor-icons/react';
import type { AccessStatus, Batch, Course, Role } from '@cvs/shared';
import { accessActionLabel } from '@cvs/shared';
import { api } from '../lib/api';
import type { LearnerRow } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import {
  AccessPill,
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Pill,
  SectionHeading,
} from '../components/ui';

const ACTION_VARIANT: Record<AccessStatus, 'primary' | 'secondary' | 'danger'> = {
  pending: 'primary',
  active: 'danger',
  revoked: 'secondary',
};

const NEXT_STATUS: Record<AccessStatus, AccessStatus> = {
  pending: 'active',
  active: 'revoked',
  revoked: 'active',
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Sentinel for the course filter. Signing up does not ask for a course, so a learner can sit
 *  on the roster enrolled in nothing — and a plain course filter hides exactly those people,
 *  who are the ones needing action. */
const NOT_ENROLLED = '__none__';

export function StudioLearners() {
  const s = useAsync(() => api.admin.learners(), []);
  const { user: me } = useAuth();
  const [query, setQuery] = useState('');
  const [courseId, setCourseId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCourseId, setBulkCourseId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Which action is awaiting confirmation, and who is still included in it. Enrolling grants
  // access to paid content, so it is confirmed as deliberately as removal is.
  const [pending, setPending] = useState<'enrol' | 'remove' | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const learners = s.data?.learners ?? [];
  const batches = s.data?.batches ?? [];
  const courses = s.data?.courses ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return learners.filter((l) => {
      if (q && !l.name.toLowerCase().includes(q) && !l.email.toLowerCase().includes(q)) return false;
      // A learner is "in" a course when they hold an active enrolment in it, which is what
      // courseIds already carries — so this reads the same truth the learner's own access does.
      if (courseId === NOT_ENROLLED) {
        if (l.role !== 'student' || l.courseIds.length > 0) return false;
      } else if (courseId && !l.courseIds.includes(courseId)) return false;
      if (batchName && l.batchName !== batchName) return false;
      return true;
    });
  }, [learners, query, courseId, batchName]);

  const narrowed = !!(query.trim() || courseId || batchName);

  // Students only: instructors hold no enrolments and are not waiting on one.
  const unenrolled = useMemo(
    () => learners.filter((l) => l.role === 'student' && l.courseIds.length === 0),
    [learners],
  );

  // Selection can only ever mean rows you can currently see. Filtering a selected learner
  // out of view drops them, so no bulk action can reach someone off-screen.
  const selectable = useMemo(() => filtered.filter((l) => l.role === 'student'), [filtered]);
  const visibleIds = useMemo(() => new Set(selectable.map((l) => l.id)), [selectable]);
  const picked = useMemo(() => [...selected].filter((id) => visibleIds.has(id)), [selected, visibleIds]);
  const allPicked = selectable.length > 0 && picked.length === selectable.length;

  function togglePick(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function pickAll(on: boolean) {
    setSelected(on ? new Set(selectable.map((l) => l.id)) : new Set());
  }

  /** Fan out one call per learner. The endpoints are idempotent and report `changed`, so the
   *  summary counts what actually happened rather than how many requests were sent. */
  async function runBulk(add: boolean) {
    // Only the learners still ticked in the confirmation dialog. Someone unticked there is
    // left alone entirely, not merely skipped in the summary.
    const targets = picked.filter((id) => !excluded.has(id));
    if (!bulkCourseId || targets.length === 0) return;
    setBulkBusy(true);
    setActionError(null);
    const course = courses.find((c) => c.id === bulkCourseId);
    const results = await Promise.allSettled(
      targets.map((id) => (add ? api.admin.enroll(id, bulkCourseId) : api.admin.unenroll(id, bulkCourseId))),
    );
    const changed = results.filter((r) => r.status === 'fulfilled' && r.value.changed).length;
    const already = results.filter((r) => r.status === 'fulfilled' && !r.value.changed).length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    // Successful writes are never rolled back to make the batch look atomic — they happened.
    const parts = [`${changed} ${add ? 'enrolled in' : 'removed from'} ${course?.title ?? 'course'}`];
    if (already) parts.push(`${already} already ${add ? 'enrolled' : 'not enrolled'}`);
    if (failed) parts.push(`${failed} failed`);
    if (failed) setActionError(parts.join(' · '));

    setSelected(new Set());
    setPending(null);
    setExcluded(new Set());
    setBulkBusy(false);
    s.reload();
  }
  const batchNames = useMemo(
    () => [...new Set(learners.map((l) => l.batchName).filter((b): b is string => !!b))].sort(),
    [learners],
  );

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  async function changeAccess(learner: LearnerRow) {
    setActionError(null);
    setBusyId(learner.id);
    try {
      await api.admin.setAccess(learner.id, NEXT_STATUS[learner.accessStatus]);
      s.reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not update access');
    } finally {
      setBusyId(null);
    }
  }

  async function changeRole(learner: LearnerRow) {
    const next: Role = learner.role === 'student' ? 'instructor' : 'student';
    const warning =
      next === 'instructor'
        ? `Make ${learner.name} an instructor? Instructors get full Studio access — content, learners and grading.`
        : `Change ${learner.name} back to a student? They lose all Studio access.`;
    if (!confirm(warning)) return;
    setActionError(null);
    setBusyId(learner.id);
    try {
      await api.admin.setRole(learner.id, next);
      s.reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not change role');
    } finally {
      setBusyId(null);
    }
  }

  async function removeMember(learner: LearnerRow) {
    if (!confirm(`Remove ${learner.name} (${learner.email})? This deletes their account and progress.`)) return;
    setActionError(null);
    setBusyId(learner.id);
    try {
      await api.admin.deleteLearner(learner.id);
      s.reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not remove member');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Learners"
        description="Manage the roster, confirm access, and add new members."
        action={
          <Button icon={UserPlus} onClick={() => setAddOpen(true)}>
            Add member
          </Button>
        }
      />

      {/* A filter only helps someone who thinks to look. Signing up never asks for a course, so
          this state accumulates quietly — a student pays, waits, and is enrolled in nothing.
          Hidden entirely once the count is zero: a permanent "0 need attention" is furniture. */}
      {unenrolled.length > 0 && courseId !== NOT_ENROLLED && (
        <Card className="flex flex-wrap items-center gap-3 border-l-[3px] border-l-amber-500 p-4">
          <Warning size={20} weight="bold" className="text-amber-600" aria-hidden="true" />
          <p className="flex-1 text-sm text-slate-700">
            <strong className="font-semibold text-brand-navy">
              {unenrolled.length} {unenrolled.length === 1 ? 'student is' : 'students are'} not enrolled in any course
            </strong>{' '}
            — sign-up does not ask for one, so they have no access until you enrol them.
          </p>
          <Button variant="ghost" onClick={() => { setCourseId(NOT_ENROLLED); setBatchName(''); setQuery(''); }}>
            Show {unenrolled.length === 1 ? 'them' : 'all'}
          </Button>
        </Card>
      )}

      <Card className="p-4">
        <label className="relative block">
          <span className="sr-only">Search learners by name or email</span>
          <MagnifyingGlass
            size={18}
            weight="bold"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="input w-full pl-10"
          />
        </label>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="flex-1">
            <span className="sr-only">Filter by course</span>
            <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className="input w-full">
              <option value="">All courses</option>
              {unenrolled.length > 0 && (
                <option value={NOT_ENROLLED}>Not enrolled in any course ({unenrolled.length})</option>
              )}
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>

          {batchNames.length > 0 && (
            <label className="flex-1">
              <span className="sr-only">Filter by batch</span>
              <select value={batchName} onChange={(e) => setBatchName(e.target.value)} className="input w-full">
                <option value="">All batches</option>
                {batchNames.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Shown only while something is narrowed, so an unfiltered roster is not captioned
              with a count that is simply the length of the list underneath it. */}
          {narrowed && (
            <div className="flex items-center gap-3 whitespace-nowrap text-sm text-slate-500">
              <span className="tabular-nums">
                {filtered.length} of {learners.length}
              </span>
              <button
                type="button"
                onClick={() => { setQuery(''); setCourseId(''); setBatchName(''); }}
                className="font-medium text-teal-700 hover:underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </Card>

      {actionError && <ErrorState message={actionError} onRetry={() => setActionError(null)} />}

      {filtered.length === 0 ? (
        <EmptyState
          icon={UserPlus}
          title={narrowed ? 'No matching members' : 'No members yet'}
          description={
            // "No members yet" beside an Add button is actively misleading when the roster is
            // full and a filter is simply excluding everyone.
            narrowed
              ? 'No one matches those filters. Try clearing one of them.'
              : 'Add your first member to get started.'
          }
          action={
            !narrowed && (
              <Button icon={UserPlus} onClick={() => setAddOpen(true)}>
                Add member
              </Button>
            )
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          {/* Header row — tablet+ */}
          <div className="hidden grid-cols-[auto_2fr_1fr_1fr_1fr_auto] gap-4 border-b border-slate-200 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 md:grid">
            <span className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brand-teal"
                checked={allPicked}
                // Native indeterminate, set through the DOM — aria-checked="mixed" alone does
                // not make a real checkbox tri-state for assistive tech.
                ref={(el) => { if (el) el.indeterminate = !allPicked && picked.length > 0; }}
                onChange={(e) => pickAll(e.target.checked)}
                disabled={selectable.length === 0}
                aria-label={`Select all ${selectable.length} listed students`}
              />
            </span>
            <span>Member</span>
            <span>Role</span>
            <span>Batch</span>
            <span>Access</span>
            <span className="text-right">Action</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {filtered.map((l) => {
              const isStudent = l.role === 'student';
              const busy = busyId === l.id;
              return (
                <li
                  key={l.id}
                  className={`grid grid-cols-1 items-center gap-3 px-5 py-4 md:grid-cols-[auto_2fr_1fr_1fr_1fr_auto] md:gap-4 ${
                    selected.has(l.id) ? 'bg-brand-teal/5' : ''
                  }`}
                >
                  <div className="hidden items-center md:flex">
                    {isStudent ? (
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand-teal"
                        checked={selected.has(l.id)}
                        onChange={() => togglePick(l.id)}
                        aria-label={`Select ${l.name}`}
                      />
                    ) : (
                      <span className="block h-4 w-4" aria-hidden="true" />
                    )}
                  </div>

                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={l.name} />
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-brand-navy">{l.name}</div>
                      <div className="truncate text-sm text-slate-500">{l.email}</div>
                      {isStudent && courses.length > 0 && (
                        <CourseChips learner={l} courses={courses} onChanged={s.reload} onError={setActionError} />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-sm text-slate-600">
                    <span className="label-mono md:hidden">Role: </span>
                    {capitalize(l.role)}
                    {me?.id !== l.id && (
                      <button
                        className="btn-ghost p-1.5 text-slate-400 hover:text-brand-teal"
                        title={l.role === 'student' ? 'Make instructor' : 'Make student'}
                        aria-label={`Change role for ${l.name}`}
                        disabled={busyId !== null && !busy}
                        onClick={() => changeRole(l)}
                      >
                        <UserSwitch size={16} weight="bold" />
                      </button>
                    )}
                  </div>

                  <div className="text-sm text-slate-600">
                    <span className="label-mono md:hidden">Batch: </span>
                    {l.batchName ?? '—'}
                  </div>

                  <div>
                    <AccessPill status={l.accessStatus} />
                  </div>

                  <div className="flex items-center gap-1 md:justify-end">
                    {isStudent ? (
                      <Button
                        variant={ACTION_VARIANT[l.accessStatus]}
                        loading={busy}
                        disabled={busyId !== null && !busy}
                        onClick={() => changeAccess(l)}
                      >
                        {accessActionLabel(l.accessStatus)}
                      </Button>
                    ) : (
                      <Pill tone="slate">Instructor</Pill>
                    )}
                    {me?.id !== l.id && (
                      <button
                        className="btn-ghost p-2 text-rose-600"
                        aria-label={`Remove ${l.name}`}
                        disabled={busyId !== null && !busy}
                        onClick={() => removeMember(l)}
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {picked.length > 0 && (
        <div
          className="sticky bottom-4 z-20 flex flex-wrap items-center gap-3 rounded-2xl bg-brand-navy px-4 py-3 text-white shadow-lg"
          // Escape cancels, matching every other dismissible surface in the app.
          onKeyDown={(e) => { if (e.key === 'Escape') { setSelected(new Set()); setPending(null); } }}
        >
          {/* Announced, not just shown — the count changes without the focus moving. */}
          <span className="whitespace-nowrap font-semibold" aria-live="polite">
            {picked.length} selected
          </span>

          <select
            value={bulkCourseId}
            onChange={(e) => setBulkCourseId(e.target.value)}
            disabled={bulkBusy}
            aria-label="Course to enrol in or remove from"
            className="min-w-[190px] flex-1 rounded-lg border border-white/25 bg-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/60"
          >
            <option value="" className="text-brand-navy">Choose a course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id} className="text-brand-navy">
                {c.title}
              </option>
            ))}
          </select>

          <Button disabled={!bulkCourseId || bulkBusy} onClick={() => { setExcluded(new Set()); setPending('enrol'); }}>
            Enrol…
          </Button>
          <button
            type="button"
            disabled={!bulkCourseId || bulkBusy}
            onClick={() => { setExcluded(new Set()); setPending('remove'); }}
            className="rounded-lg border border-white/30 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Remove…
          </button>
          <button type="button" className="text-sm text-white/70 hover:text-white" onClick={() => setSelected(new Set())}>
            Clear
          </button>
        </div>
      )}

      <ConfirmEnrolmentModal
        open={pending !== null}
        action={pending ?? 'enrol'}
        course={courses.find((c) => c.id === bulkCourseId) ?? null}
        learners={learners.filter((l) => picked.includes(l.id))}
        excluded={excluded}
        onToggle={(id) =>
          setExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        busy={bulkBusy}
        onClose={() => { setPending(null); setExcluded(new Set()); }}
        onConfirm={() => runBulk(pending === 'enrol')}
      />

      <AddMemberModal
        open={addOpen}
        batches={batches}
        courses={courses}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          s.reload();
        }}
      />
    </div>
  );
}

// ── Per-learner course enrollment (toggle chips) ─────────────────────────────
function CourseChips({
  learner,
  courses,
  onChanged,
  onError,
}: {
  learner: LearnerRow;
  courses: Course[];
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const enrolled = new Set(learner.courseIds);

  async function toggle(courseId: string) {
    setBusy(courseId);
    try {
      if (enrolled.has(courseId)) await api.admin.unenroll(learner.id, courseId);
      else await api.admin.enroll(learner.id, courseId);
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not update enrollment');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {courses.map((c) => {
        const on = enrolled.has(c.id);
        return (
          <button
            key={c.id}
            type="button"
            disabled={busy !== null}
            onClick={() => toggle(c.id)}
            title={on ? `Enrolled in ${c.title} — click to remove` : `Enroll in ${c.title}`}
            className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition ${
              on
                ? 'border-brand-teal bg-brand-teal/10 text-brand-teal'
                : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600'
            } ${busy === c.id ? 'opacity-60' : ''}`}
          >
            {c.title}
          </button>
        );
      })}
    </div>
  );
}

// ── Add member modal ─────────────────────────────────────────────────────────
function AddMemberModal({
  open,
  batches,
  courses,
  onClose,
  onAdded,
}: {
  open: boolean;
  batches: Batch[];
  courses: Course[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('student');
  const [batchId, setBatchId] = useState<string>('');
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setEmail('');
    setCourseIds([]);
    setRole('student');
    setBatchId('');
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.admin.addLearner({
        name: name.trim(),
        email: email.trim(),
        role,
        batchId: batchId || null,
        // Sent with the create, not after it. The server validates every course before it
        // writes the user, so a member can never land on the roster missing the access
        // they were added for.
        courseIds: role === 'student' ? courseIds : [],
      });
      reset();
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add member');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add member"
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="add-member-form" loading={submitting}>
            Add member
          </Button>
        </>
      }
    >
      <form id="add-member-form" onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="member-name" className="label-mono mb-1 block">
            Name
          </label>
          <input
            id="member-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className="input w-full"
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="member-email" className="label-mono mb-1 block">
            Email
          </label>
          <input
            id="member-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            className="input w-full"
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="member-role" className="label-mono mb-1 block">
              Role
            </label>
            <select
              id="member-role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="input w-full"
            >
              <option value="student">Student</option>
              <option value="instructor">Instructor</option>
            </select>
          </div>

          <div>
            <label htmlFor="member-batch" className="label-mono mb-1 block">
              Batch <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <select
              id="member-batch"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              className="input w-full"
            >
              <option value="">No batch</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Only students are enrolled, so the section disappears for instructors rather than
            offering a choice that would be silently discarded. */}
        {role === 'student' && courses.length > 0 && (
          <fieldset className="mt-4">
            <legend className="label-mono mb-2">
              Courses <span className="font-normal normal-case text-slate-400">(optional)</span>
            </legend>
            <div className="space-y-2">
              {courses.map((c) => (
                <label key={c.id} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-teal"
                    checked={courseIds.includes(c.id)}
                    onChange={(e) =>
                      setCourseIds((prev) => (e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id)))
                    }
                  />
                  {c.title}
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">
              They can still be enrolled later from the roster.
            </p>
          </fieldset>
        )}

        {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
      </form>
    </Modal>
  );
}

// ── Confirming a bulk enrolment ──────────────────────────────────────────────
/**
 * Verification, not a speed bump.
 *
 * A dialog that only asks "are you sure?" adds a click and catches nothing — the admin already
 * believed they were right, which is why they pressed the button. This one shows exactly WHO is
 * about to change and what will happen to each of them, and lets anyone be unticked without
 * abandoning the batch. That is the difference between confirming an intention and checking a
 * fact, and enrolment is worth checking: it grants access to paid course content.
 */
function ConfirmEnrolmentModal({
  open,
  action,
  course,
  learners,
  excluded,
  onToggle,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  action: 'enrol' | 'remove';
  course: Course | null;
  learners: LearnerRow[];
  excluded: Set<string>;
  onToggle: (id: string) => void;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!open || !course) return null;
  const adding = action === 'enrol';

  const included = learners.filter((l) => !excluded.has(l.id));
  // Someone already in the desired state is a no-op, and saying so up front stops the summary
  // afterwards from looking like the action half-failed.
  const willChange = included.filter((l) => (adding ? !l.courseIds.includes(course.id) : l.courseIds.includes(course.id)));
  const noOp = included.length - willChange.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={adding ? `Enrol in ${course.title}` : `Remove from ${course.title}`}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-slate-500">
            {willChange.length} will change
            {noOp > 0 && ` · ${noOp} already ${adding ? 'enrolled' : 'not enrolled'}`}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant={adding ? 'primary' : 'danger'}
              onClick={onConfirm}
              disabled={busy || willChange.length === 0}
            >
              {busy
                ? adding ? 'Enrolling…' : 'Removing…'
                : adding ? `Enrol ${willChange.length}` : `Remove ${willChange.length}`}
            </Button>
          </div>
        </div>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        {adding
          ? 'These learners will get access to the course as soon as you confirm.'
          : 'Access is revoked, not deleted — their progress is kept and re-enrolling restores it.'}
      </p>

      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
        {learners.map((l) => {
          const enrolled = l.courseIds.includes(course.id);
          const changes = adding ? !enrolled : enrolled;
          return (
            <li key={l.id} className="flex items-center gap-3 px-3 py-2.5">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-brand-teal"
                checked={!excluded.has(l.id)}
                onChange={() => onToggle(l.id)}
                disabled={busy}
                aria-label={`Include ${l.name}`}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-brand-navy">{l.name}</div>
                <div className="truncate text-xs text-slate-500">{l.email}</div>
              </div>
              {!changes && (
                <span className="shrink-0 text-xs text-slate-400">
                  {adding ? 'Already enrolled' : 'Not enrolled'}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
